import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

export const HelpDialog = ({ open, onOpenChange }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="help-dialog"
        className="max-w-2xl bg-[#0f1015] border-zinc-800"
      >
        <DialogHeader>
          <DialogTitle className="text-zinc-100 text-2xl">Help &amp; Controls</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Everything you can do with the Height Map Color Generator.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4 -mr-4">
          <div className="space-y-6 text-sm text-zinc-300 leading-relaxed pb-2">
            <Section title="Uploading a height map">
              <p>
                Drag &amp; drop any grayscale PNG / JPG / WEBP onto the drop zone, or click it to browse. Non-grayscale images are automatically converted using luminance so they still work.
              </p>
              <p>
                Need real-world terrain? Grab free, fast height maps from{" "}
                <a
                  data-testid="heightmap-source-link"
                  href="https://3d-map-generator.com/free-heightmap-generator/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mono text-rose-400 underline underline-offset-2 hover:text-rose-300"
                >
                  3d-map-generator.com&apos;s free heightmap generator
                </a>{" "}
                &mdash; pick any spot on Earth and download a grayscale height map to drop in here.
              </p>
            </Section>

            <Section title="Color modes">
              <p>
                <strong className="text-zinc-100">Elevation</strong> assigns materials by height and smoothly interpolates between the mid-points of non-solid layers. Solid layers cut through the gradient with hard edges.
              </p>
              <p>
                <strong className="text-zinc-100">Manual Paint</strong> snaps every pixel to the layer whose band it falls into, and unlocks hand tools: a hard Solid Brush, a soft Gradient Brush and a Fill Bucket. Anything you paint here is kept on a locked overlay that also shows in Elevation, Material Mix, and Steep Slopes — layer/slider edits never touch painted pixels.
              </p>
              <p>
                <strong className="text-zinc-100">Material Mix</strong> combines two or more materials into one layer using a pattern (noise, checker, stripes, dots, or your own imported patterns) with adjustable scale, rotation and density.
              </p>
              <p>
                <strong className="text-zinc-100">Steep Slopes</strong> is recommended as the final step. It applies a selected material to terrain at or above an estimated angle for cliffs, or at or below that angle for flatter ground. Add one or more protected existing materials to leave their pixels unaffected. The live mask shows exactly which pixels will change and which are protected.
              </p>
            </Section>

            <Section title="Solid vs. gradient (All Solid)">
              <p>
                The <strong className="text-zinc-100">All Solid Colors</strong> switch at the top of Height Layers flips every layer at once. <strong className="text-zinc-100">On</strong> = flat, hard-edged bands. <strong className="text-zinc-100">Off</strong> = the colors blur/blend into each other for smooth transitions.
              </p>
              <p>
                With All Solid off you can then toggle any single layer back to solid so it stays a hard band while the rest blend.
              </p>
            </Section>

            <Section title="Height layers">
              <p>
                Each layer has a color, a name, a solid toggle and a <strong className="text-zinc-100">Min / Max</strong> dual-slider covering 0-255 grayscale.
              </p>
              <p>
                Layers move independently &mdash; a layer&apos;s Max can go past another layer&apos;s Min without deleting or overriding it. Overlapping bands are painted in the order shown in the strip, top-most first. Hover any segment of the palette strip to see its coverage percentage.
              </p>
            </Section>

            <Section title="Picking a color code">
              <p>Click the color swatch on any layer to open the built-in picker: a saturation box, a hue slider, a HEX field, and a <strong className="text-zinc-100">Recent colours</strong> history that remembers what you&apos;ve used.</p>
              <p>Roblox Studio RGB values work directly &mdash; type the hex, e.g. <code className="mono text-rose-400">rgb(12, 84, 92)</code> = <code className="mono text-rose-400">#0c545c</code>.</p>
            </Section>

            <Section title="Reference layer">
              <p>
                Toggle <strong className="text-zinc-100">Reference</strong> in the toolbar to overlay the original grayscale height map on top of every color map, so you can line features up. Hover the toggle to drag its <strong className="text-zinc-100">opacity</strong> slider.
              </p>
            </Section>

            <Section title="Pattern maker (Experimental)">
              <p>
                In <strong className="text-zinc-100">Settings</strong>, the Pattern maker lets you import a black &amp; white image as a custom Material Mix pattern &mdash; <strong className="text-zinc-100">white</strong> areas become the base material and <strong className="text-zinc-100">black</strong> areas become the pattern. Design it in any image editor, then import it (you can&apos;t draw it in the app). Saved patterns appear in the Material Mix pattern list.
              </p>
            </Section>

            <Section title="Presets">
              <p>
                The <strong className="text-zinc-100">Presets</strong> button opens a menu of saved palettes. Roblox Terrain is always available and loaded by default. Use <strong className="text-zinc-100">Save current as preset</strong> to store your own, and the arrow icons on the Height Layers panel to export/import presets as JSON.
              </p>
            </Section>

            <Section title="3D preview &amp; sharing">
              <p>
                <strong className="text-zinc-100">3D</strong> in the top bar previews your height map as terrain with the generated colors applied. <strong className="text-zinc-100">Share</strong> copies a link that recreates your current palette.
              </p>
            </Section>

            <Section title="Exporting">
              <p>
                <strong className="text-zinc-100">Download Color Map</strong> is a menu &mdash; export as <strong className="text-zinc-100">PNG</strong> (transparent), <strong className="text-zinc-100">JPG</strong> (smaller, flattened) or <strong className="text-zinc-100">WEBP</strong>, all at your source resolution.
              </p>
            </Section>

            <Section title="Navigation &amp; settings">
              <p>
                Scroll to zoom any canvas &mdash; it zooms toward your cursor. Right-click + drag pans. <kbd className="mono text-rose-400">Ctrl/Cmd+Z</kbd> undoes. The gear icon adjusts UI density: bigger viewports, hide the how-it-works panel, layer-list height, the palette strip, and the experimental Import Color Map button.
              </p>
            </Section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-zinc-100 font-semibold text-base mb-2">{title}</h3>
    <div className="space-y-2 text-zinc-400">{children}</div>
  </div>
);
